<template>
    <div class="pd-salary-lane">
        <!-- Missing inputs: same CTA box as "Track Your Renewal" -->
        <div v-if="!salary.ready" class="info-box">
            <div class="box-main">
                <div class="box-icon"><i class="fad fa-chart-line-up fa-2x has-text-primary"></i></div>
                <div class="box-body">
                    <div class="box-title">See Your Next Salary Column</div>
                    <div class="box-text">Add your district, degree and current clock-hour column and we'll show how close you are to your next salary column, and what it's worth.</div>
                </div>
            </div>
            <div class="box-actions">
                <div class="box-buttons">
                    <b-button type="is-primary" size="is-small" outlined @click="$emit('profile')"><i class="fas fa-plus mr-2"></i>Add Career Details</b-button>
                </div>
            </div>
        </div>

        <div v-else-if="loading" class="lane-note has-text-grey"><i class="fas fa-spinner fa-spin mr-2"></i>Checking {{ salary.district_name }}'s salary schedule…</div>

        <!-- No published schedule -->
        <div v-else-if="!scale" class="lane-note">
            <i class="far fa-chart-line-up mr-2"></i>We don't have {{ salary.district_name || 'your district' }}'s salary schedule on file yet, so we can't size your next column jump. The Bundle Builder can still help you plan your hours.
        </div>

        <!-- At the top already -->
        <div v-else-if="position && !target" class="lane-note">
            <i class="fas fa-trophy has-text-warning mr-2"></i>You're at the top of {{ salary.district_name }}'s {{ salary.highest_degree }} track — every clock hour still counts toward your renewal.
        </div>

        <div v-else-if="target">
            <div class="lane-line">
                <i class="far fa-chart-line-up mr-2"></i>You're about <strong>{{ target.requiredHours }} hours</strong> from <strong>{{ target.tier }}</strong>
                <span v-if="target.diff > 0"> &mdash; worth <strong class="has-text-success">+{{ target.diff | money }}</strong> a year on your pay</span>.
            </div>
            <div class="lane-actions mt-2">
                <a href @click.prevent="showPitch = !showPitch"><i :class="['far', showPitch ? 'fa-chevron-up' : 'fa-chevron-down', 'mr-1']"></i>{{ showPitch ? 'Hide' : 'See' }} what moving up is worth</a>
            </div>
            <package-pitch
                v-if="showPitch"
                class="mt-4"
                :scale="scale"
                :scale-loading="false"
                :has-district="true"
                :district-id="salary.district_id"
                :district-name="salary.district_name || ''"
                :position="position"
                :target="target"
                :pkg-hours="0"
            />
        </div>
    </div>
</template>

<script>
    import {client as http} from "../../http_client";
    import PackagePitch from "../package/PackagePitch";
    import {computePosition} from "../package/scaleMath";

    /**
     * "You're N hours from your next column" — the bundle builder's salary
     * math, run from the profile the student already gave us (district,
     * degree, current column, years). The gap uses the declared column
     * only; the builder recomputes when they actually buy. Purely
     * informational: the bundle call to action lives in PdSection.
     */
    export default {
        name: "PdSalaryLane",
        components: {PackagePitch},
        props: {
            salary: {type: Object, required: true},
        },
        data() {
            return {scale: null, loading: false, showPitch: false};
        },
        computed: {
            position() {
                if (!this.scale || !this.salary.ready) return null;
                return computePosition(this.scale, this.salary.highest_degree, this.salary.hours_column, this.salary.years_teaching);
            },
            target() {
                const targets = this.position && this.position.targets ? this.position.targets : [];
                return targets.length ? targets[0] : null;
            }
        },
        methods: {
            load() {
                if (!this.salary.ready || !this.salary.district_id) return;
                this.loading = true;
                http.get('districts/' + this.salary.district_id + '/pay-scale').then(response => {
                    this.scale = response.data;
                    this.loading = false;
                }).catch(() => {
                    // 404 = no published schedule for this district
                    this.scale = null;
                    this.loading = false;
                });
            }
        },
        watch: {
            'salary.district_id'() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .lane-note, .lane-line {
        font-size: 0.95rem;
        color: #33475b;
    }

    .lane-actions {
        font-size: 0.85rem;
        font-weight: 600;
    }
</style>
